/**
 * EntryRow — the shared clickable plan row (tentpole sections, custom-moments
 * section, orphans, By date day groups, month-grid day modal).
 *
 * Two lines so one thing leads: the headline is the targeted channel and the
 * plan title; a quiet meta line (planMetaParts) carries everything else. The
 * moment and review status sit in fixed-width columns so they line up from
 * row to row. Approved is the everyday state and renders quiet; declined,
 * withdrawn, paused and cancelled plans recede as a whole.
 *
 * - `showDate`: a leading date column, for rows that aren't grouped by day.
 * - `showMoment`: a moment chip, for rows that can't inherit the moment from
 *   a section header (✦ for custom moments, the tentpole otherwise).
 * - `compact`: narrow containers (the day modal) — no fixed columns; the
 *   moment and status move onto the row's own lines.
 */

import React, { useState } from 'react';
import { tableStyles } from '../../ui';
import ReviewStatusPill from './ReviewStatusPill';
import { CalendarEntry, Tentpole } from './types';
import {
  entryDisplayParts,
  fmtPlannedRange,
  fmtShortPlanned,
  planMetaParts,
  planStatusOf,
  tentpoleLabel,
  todayYmd,
} from './shared';

interface EntryRowProps {
  entry: CalendarEntry;
  /** Resolved by the caller from tentpoleBySlug — only used when showMoment. */
  tentpole?: Tentpole;
  /** Rows outside a tentpole section carry their own moment context. */
  showMoment?: boolean;
  /** Rows that aren't grouped under a day header carry their own date. */
  showDate?: boolean;
  /** Narrow containers: stack instead of aligning columns. */
  compact?: boolean;
  onOpen: (id: string) => void;
}

const DATE_COLUMN = 104;
const MOMENT_COLUMN = 200;
const STATUS_COLUMN = 116;

/** Plans that are off the table read quieter than live ones. */
export const isRecededPlan = (entry: CalendarEntry): boolean => {
  const planStatus = planStatusOf(entry);
  return entry.status === 'declined' || entry.status === 'withdrawn'
    || planStatus === 'paused' || planStatus === 'cancelled';
};

/** The one moment chip shape: tentpoles in the tentpole tint, custom moments
 *  (✦) in the featured tint, so the two read as the same kind of thing. */
export const MomentChip: React.FC<{ entry: CalendarEntry; tentpole?: Tentpole }> = ({ entry, tentpole }) => {
  const custom = !!entry.customLabel;
  const label = custom
    ? `✦ ${entry.customLabel}`
    : tentpole ? tentpoleLabel(tentpole) : entry.tentpoleSlug ?? '';
  if (!label) return null;
  return (
    <span title={label} style={custom ? customChipStyle : tentpoleChipStyle}>
      {label}
    </span>
  );
};

const EntryRow: React.FC<EntryRowProps> = ({ entry, tentpole, showMoment, showDate, compact, onOpen }) => {
  const [hover, setHover] = useState(false);

  const { primary } = entryDisplayParts(entry);
  const meta = planMetaParts(entry, { showsDates: !!showDate && !compact });
  const receded = isRecededPlan(entry);
  const startYear = entry.plannedDate.slice(0, 4);

  const headline = (
    <div style={{ ...headlineStyle, color: receded ? 'var(--fg-3)' : 'var(--fg-1)' }}>
      <span style={{ fontWeight: 600 }}>{primary}</span>{' '}
      <span style={{ marginLeft: 4 }}>{entry.title}</span>
    </div>
  );

  const interaction = {
    role: 'button',
    tabIndex: 0,
    onClick: () => onOpen(entry.id),
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(entry.id); }
    },
    onMouseEnter: () => setHover(true),
    onMouseLeave: () => setHover(false),
  } as const;
  const background = hover ? tableStyles.rowHoverBackground : 'transparent';

  if (compact) {
    return (
      <div {...interaction} style={{ ...compactRowStyle, background }}>
        <div style={compactTopStyle}>
          <div style={{ flex: 1, minWidth: 0 }}>{headline}</div>
          <ReviewStatusPill status={entry.status} />
        </div>
        <div style={compactMetaStyle}>
          {showMoment && <MomentChip entry={entry} tentpole={tentpole} />}
          {meta.length > 0 && <span>{meta.join(' · ')}</span>}
        </div>
      </div>
    );
  }

  const columns = [
    showDate ? `${DATE_COLUMN}px` : null,
    'minmax(0, 1fr)',
    showMoment ? `${MOMENT_COLUMN}px` : null,
    `${STATUS_COLUMN}px`,
  ].filter(Boolean).join(' ');

  return (
    <div {...interaction} style={{ ...rowStyle, gridTemplateColumns: columns, background }}>
      {showDate && (
        <div style={dateCellStyle} title={fmtPlannedRange(entry)}>
          <div style={{ color: receded ? 'var(--fg-3)' : 'var(--fg-2)' }}>{fmtShortPlanned(entry)}</div>
          {startYear !== todayYmd().slice(0, 4) && <div style={yearStyle}>{startYear}</div>}
        </div>
      )}
      <div style={{ minWidth: 0 }}>
        {headline}
        {meta.length > 0 && <div style={metaStyle}>{meta.join(' · ')}</div>}
      </div>
      {showMoment && (
        <div style={{ minWidth: 0 }}>
          <MomentChip entry={entry} tentpole={tentpole} />
        </div>
      )}
      <div style={{ textAlign: 'right' }}>
        <ReviewStatusPill status={entry.status} />
      </div>
    </div>
  );
};

const rowStyle: React.CSSProperties = {
  display: 'grid',
  columnGap: 16,
  alignItems: 'center',
  padding: '10px',
  margin: '0 -10px',
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
  fontFamily: 'var(--font-sans)',
};

const headlineStyle: React.CSSProperties = {
  fontSize: 14,
  lineHeight: 1.35,
};

const metaStyle: React.CSSProperties = {
  marginTop: 3,
  fontSize: 12.5,
  lineHeight: 1.35,
  color: 'var(--fg-3)',
};

const dateCellStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  lineHeight: 1.3,
  fontVariantNumeric: 'tabular-nums',
};

const yearStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--fg-3)',
};

const chipBaseStyle: React.CSSProperties = {
  display: 'inline-block',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
  padding: '3px 9px',
  borderRadius: 'var(--radius-pill)',
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1.35,
};

const tentpoleChipStyle: React.CSSProperties = {
  ...chipBaseStyle,
  background: 'var(--calendar-tentpole-soft)',
  color: 'var(--calendar-tentpole)',
};

const customChipStyle: React.CSSProperties = {
  ...chipBaseStyle,
  background: 'var(--accent-2-soft)',
  color: 'var(--accent-2-soft-fg)',
};

const compactRowStyle: React.CSSProperties = {
  padding: '10px',
  margin: '0 -10px',
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
  fontFamily: 'var(--font-sans)',
};

const compactTopStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
};

const compactMetaStyle: React.CSSProperties = {
  ...metaStyle,
  marginTop: 5,
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 8,
};

export default EntryRow;
